import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import ModalConfirm from '~/components/Shared/ModalConfirm';
import FileIcon from '~/assets/svg/newSvg/file.svg?react';
import DeleteSvg from '~/assets/svg/newSvg/delete.svg?react';
import DownloadSvg from '~/assets/svg/newSvg/downloadQr.svg?react';
import ViewSvg from '~/assets/svg/newSvg/eye-white.svg?react';
import { IconButton } from '~/ui';
import cn from 'classnames';
import styles from './AssetStorageFiles.module.scss';
import { useTranslation } from 'react-i18next';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { useEffect, useState } from 'react';

export interface IFileProps {
  id: number;
  name: string;
  path: string;
  type: string;
  asset_id: number;
  folder_id: number;
  full_path: string;
}

interface IFilesData {
  data: IFileProps[];
}

interface IAssetStorageFilesProps {
  folderId: string | undefined;
  setSelectedFile: (file: IFileProps) => void;
}

const isImageFile = [
  'webp',
  'png',
  'jpg',
  'jpeg',
  'ico',
  'icon',
  'bmp',
  'gif',
  'svg',
  'tiff',
  'avif',
  'jfif',
];

export const AssetStorageFiles = ({
  folderId,
  setSelectedFile,
}: IAssetStorageFilesProps) => {
  const { t } = useTranslation();
  const { assetId } = useParams();
  const [filesArr, setFilesArr] = useState<IFileProps[]>([]);
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const client = useQueryClient();

  const { data: folderFiles, refetch } = useQuery<IFilesData>({
    queryFn: async () => {
      const { data } = await instance.get('admin/documents', {
        params: { asset_id: assetId, perPage: 100 },
      });
      return data;
    },
    queryKey: ['documents', assetId, folderId],
  });

  const { mutateAsync: deleteFile } = useMutation(
    async (fileId: number) => {
      const res = await instance.delete(`admin/documents/${fileId}`);
      return res;
    },
    {
      onSuccess: () => {
        client.invalidateQueries(['documents']);
        toastSuccess(t('file_successfully_deleted'));
        refetch();
      },
      onError: error => {
        toastError(error);
      },
    }
  );

  useEffect(() => {
    if (folderFiles) {
      const files = folderFiles?.data?.filter(
        item => item.folder_id === Number(folderId)
      );
      setFilesArr(files);
    }
  }, [folderId, folderFiles]);

  const handleDownloadFile = async (file: IFileProps) => {
    try {
      const response = await fetch(file.full_path);
      if (!response.ok) throw new Error('Ошибка при загрузке файла');

      const blob = await response.blob();

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Ошибка при скачивании файла:', error);
    }
  };

  return (
    <div className={styles.filesWrapper}>
      <div className={styles.folderTitleWrapper}>
        <p className={styles.folderTitle}>{t('files')}</p>
      </div>
      {filesArr && filesArr.length ? (
        <ul className={styles.filesList}>
          {folderId &&
            filesArr?.map(item => (
              <li key={item.id} className={styles.fileItem}>
                <div
                  className={cn(styles.imageWrapper, {
                    [styles.purpleBg]: !isImageFile.includes(
                      item.type.toLowerCase()
                    ),
                  })}
                >
                  {isImageFile.includes(item.type.toLowerCase()) ? (
                    <img src={item.full_path} className={styles.img} />
                  ) : (
                    <FileIcon />
                  )}
                  <ModalConfirm
                    title={t('sure_delete_file')}
                    description={t('impossible_restore_file')}
                    onConfirm={() => {
                      deleteFile(item.id);
                    }}
                  >
                    {open => (
                      <IconButton onClick={open} className={styles.deleteBtn}>
                        <DeleteSvg className={styles.deleteSvg} />
                      </IconButton>
                    )}
                  </ModalConfirm>
                  <IconButton
                    onClick={() => {
                      handleDownloadFile(item);
                    }}
                    className={styles.downloadBtn}
                  >
                    <DownloadSvg className={styles.downloadSvg} />
                  </IconButton>
                  {isImageFile.includes(item.type.toLowerCase()) && <IconButton
                    onClick={() => {
                      setSelectedFile(item);
                    }}
                    className={styles.viewBtn}
                  >
                    <ViewSvg className={styles.viewSvg} />
                  </IconButton>}
                </div>

                <p className={styles.fileName}>{`${item.name}`}</p>
              </li>
            ))}
        </ul>
      ) : (
        <div className={styles.emptyWrapper}>
          <p className={styles.emptyInfo}>{t('empty')}</p>
        </div>
      )}
    </div>
  );
};
